/**
 * Storico di tutti i resoconti (analisi AI sui titoli + briefing aggregati di
 * mercato), filtrabile e ordinabile.
 *
 * ## Perché su schermo stretto non è più una tabella (F-091)
 *
 * Una tabella è una griglia perché si confrontano le righe **lungo le colonne**.
 * Su un telefono le colonne non stanno affiancate, quindi la griglia smette di
 * ripagare il proprio costo: si paga l'ingombro senza ottenere il confronto.
 * Il risultato osservato era esattamente questo — la data andava a capo su
 * quattro righe («26 / ago / 2026, / 11:41») rendendo ogni riga alta come una
 * scheda, e «Titolo/Mercato», cioè **la colonna che si tocca**, era quella che
 * cadeva fuori dal bordo.
 *
 * Sotto gli 880px la stessa lista diventa perciò un elenco di **schede**, con
 * il titolo come prima riga. Sopra resta la tabella, dove ordinare e
 * confrontare ha senso. Le due strutture convivono nel DOM e la scelta la fa il
 * CSS: senza `matchMedia` (che in jsdom non esiste, cfr. `theme-context.ts`) la
 * decisione resterebbe altrimenti in uno stato JavaScript non verificabile nei
 * test.
 *
 * ## I filtri si chiudono, il loro effetto no
 *
 * Occupavano l'intera prima schermata prima di un solo risultato, ed è la parte
 * secondaria della pagina: qui si viene per leggere l'elenco. Su mobile il
 * pannello è quindi richiudibile — ma **i filtri attivi restano visibili come
 * chip anche da chiuso**, e sono removibili da lì. Nascondere i comandi è
 * comodo; nascondere il fatto che l'elenco è filtrato lo farebbe sembrare
 * completo quando non lo è, ed è l'unica cosa che questa pagina non può fare.
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Card, EmptyState, Select, Spinner, TableWrap } from '@/ui';
import { useReports, type ReportKind, type ReportListItem } from './api';
import {
  formatReportDate,
  KIND_LABELS,
  KIND_TONE,
  reportTypeDetail,
  STATUS_LABELS,
  STATUS_TONE,
  TRIGGERED_BY_LABELS,
} from './report-format';
import { RATING_TONE } from '@/features/watchlist/analysis-rating';
import './reports.css';

type SortKey = 'date' | 'market' | 'type' | 'status';
type SortOrder = 'asc' | 'desc';

const SORT_COLUMNS: Array<{ key: SortKey; label: string; className?: string }> = [
  { key: 'date', label: 'Data' },
  { key: 'type', label: 'Tipo' },
  { key: 'market', label: 'Mercato', className: 'col--secondary' },
  { key: 'status', label: 'Stato' },
];

const KIND_OPTIONS: Array<{ value: ReportKind; label: string }> = [
  { value: 'analysis', label: KIND_LABELS.analysis },
  { value: 'briefing', label: KIND_LABELS.briefing },
  { value: 'digest', label: KIND_LABELS.digest },
  { value: 'rotation', label: KIND_LABELS.rotation },
];

function sortValue(item: ReportListItem, key: SortKey): string | number {
  switch (key) {
    case 'date':
      return new Date(item.createdAt).getTime();
    case 'market':
      return item.marketMic ?? '';
    case 'type':
      return item.kind;
    case 'status':
      return item.status;
  }
}

function SortHeader({
  column,
  active,
  order,
  onClick,
}: {
  column: { key: SortKey; label: string; className?: string };
  active: boolean;
  order: SortOrder;
  onClick: () => void;
}) {
  return (
    <th className={column.className}>
      <button type="button" className="reports__sort-button" onClick={onClick}>
        {column.label}
        {active && <span aria-hidden="true">{order === 'asc' ? ' ▲' : ' ▼'}</span>}
      </button>
    </th>
  );
}

/**
 * La riconciliazione di questo resoconto (F-105).
 *
 * `kind` e `id` bastano: sono la coppia con cui il backend sa già distinguere i
 * quattro generi, e ricalcolarla qui da `detailPath` significherebbe
 * interpretare una stringa costruita altrove.
 */
function tracePath(item: ReportListItem): string {
  return `/reports/${item.kind}/${item.id}/trace`;
}

function InstrumentChips({ instruments }: { instruments: ReportListItem['instruments'] }) {
  if (instruments.length === 0) return <span className="text-muted">—</span>;
  return (
    <div className="reports__chips">
      {instruments.map((instrument) => (
        // Chip → scheda del titolo (SPEC-016): dal resoconto al titolo in un click.
        <Link
          key={instrument.id}
          to={`/instruments/${instrument.id}`}
          className="reports__chip"
          title={instrument.isin ?? instrument.symbol ?? undefined}
        >
          {instrument.symbol ?? instrument.name}
        </Link>
      ))}
    </div>
  );
}

/**
 * Una scheda per resoconto: la forma della lista sotto gli 880px.
 *
 * Lo **stato compare solo quando non è «completato»**. Su quaranta righe
 * completate un badge verde ripetuto quaranta volte si smette di leggere — e
 * con esso si smetterebbe di notare le due che dicono «Fallito». Mostrare le
 * sole eccezioni è più informativo, non meno: è la stessa ragione per cui la
 * riga di dettaglio del tipo sparisce quando ripete il badge sopra di sé.
 */
function ReportCard({ item }: { item: ReportListItem }) {
  const detail = reportTypeDetail(item.kind, item.reportType);
  const showStatus = item.status !== 'completed';

  return (
    <li className="reports__card">
      {/* Il titolo per primo: è ciò che si legge e ciò che si tocca. Nella
          tabella era la quinta colonna, cioè quella che cadeva fuori. */}
      <Link to={item.detailPath} className="reports__card-title">
        {item.title}
      </Link>
      <Link to={tracePath(item)} className="reports__trace-link">
        Catena →
      </Link>

      <div className="reports__card-meta">
        <Badge tone={KIND_TONE[item.kind]}>{KIND_LABELS[item.kind]}</Badge>
        {detail && <span className="reports__card-detail">{detail}</span>}
        <time className="reports__card-date" dateTime={item.createdAt}>
          {formatReportDate(item.createdAt)}
        </time>
      </div>

      {(showStatus || item.rating || item.marketMic) && (
        <div className="reports__card-meta">
          {showStatus && (
            <Badge tone={STATUS_TONE[item.status] ?? 'neutral'}>
              {STATUS_LABELS[item.status] ?? item.status}
            </Badge>
          )}
          {item.rating && (
            <Badge tone={RATING_TONE[item.rating as keyof typeof RATING_TONE]}>{item.rating}</Badge>
          )}
          {item.marketMic && <span className="reports__card-market">{item.marketMic}</span>}
        </div>
      )}

      {item.instruments.length > 0 && <InstrumentChips instruments={item.instruments} />}
    </li>
  );
}

export function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportsQuery = useReports();

  const [kindFilter, setKindFilter] = useState<ReportKind | ''>('');
  const [statusFilter, setStatusFilter] = useState('');
  const micFilter = searchParams.get('mic') ?? '';
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  /** Solo per schermo stretto: sopra gli 880px il pannello è sempre aperto via CSS. */
  const [filtersOpen, setFiltersOpen] = useState(false);

  const items = useMemo(() => reportsQuery.data?.items ?? [], [reportsQuery.data]);

  const markets = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      if (item.marketMic) map.set(item.marketMic, item.marketName ?? item.marketMic);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const statuses = useMemo(() => [...new Set(items.map((item) => item.status))].sort(), [items]);

  const filtered = useMemo(() => {
    return items
      .filter((item) => !kindFilter || item.kind === kindFilter)
      .filter((item) => !statusFilter || item.status === statusFilter)
      .filter((item) => !micFilter || item.marketMic === micFilter);
  }, [items, kindFilter, statusFilter, micFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortOrder]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const clearMicFilter = () =>
    setSearchParams((params) => {
      params.delete('mic');
      return params;
    });

  /**
   * I filtri attivi, in forma removibile. Vivono **fuori** dal pannello
   * richiudibile: chiudere i comandi è comodo, far sparire il fatto che
   * l'elenco è filtrato lo farebbe sembrare completo quando non lo è.
   */
  const activeFilters: Array<{ key: string; label: string; clear: () => void }> = [
    ...(kindFilter
      ? [{ key: 'kind', label: KIND_LABELS[kindFilter], clear: () => setKindFilter('') }]
      : []),
    ...(micFilter ? [{ key: 'mic', label: micFilter, clear: clearMicFilter }] : []),
    ...(statusFilter
      ? [
          {
            key: 'status',
            label: STATUS_LABELS[statusFilter] ?? statusFilter,
            clear: () => setStatusFilter(''),
          },
        ]
      : []),
  ];

  return (
    <div className="reports">
      <header className="reports__header">
        <div>
          <h1 className="reports__title">Resoconti</h1>
          <p className="reports__subtitle">
            Storico di tutte le analisi AI sui titoli e dei briefing aggregati di mercato
          </p>
        </div>
      </header>

      <Card flush>
        <div className="reports__filters-bar">
          <button
            type="button"
            className="reports__filters-toggle"
            aria-expanded={filtersOpen}
            aria-controls="reports-filters"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            Filtri
            {activeFilters.length > 0 && (
              <span className="reports__filters-count">{activeFilters.length}</span>
            )}
            <span aria-hidden="true">{filtersOpen ? ' ▲' : ' ▼'}</span>
          </button>

          {activeFilters.length > 0 && (
            <ul className="reports__active-filters">
              {activeFilters.map((filter) => (
                <li key={filter.key}>
                  <button
                    type="button"
                    className="reports__active-filter"
                    onClick={filter.clear}
                    aria-label={`Rimuovi filtro ${filter.label}`}
                  >
                    {filter.label} <span aria-hidden="true">✕</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          id="reports-filters"
          className={`reports__filters ${filtersOpen ? 'reports__filters--open' : ''}`}
        >
          <Select
            label="Tipo"
            value={kindFilter}
            onChange={(event) => setKindFilter(event.target.value as ReportKind | '')}
            placeholder="Tutti"
            options={KIND_OPTIONS}
          />
          <Select
            label="Mercato"
            value={micFilter}
            onChange={(event) => setSearchParams(event.target.value ? { mic: event.target.value } : {})}
            placeholder="Tutti"
            options={markets.map(([mic, name]) => ({ value: mic, label: `${mic} — ${name}` }))}
          />
          <Select
            label="Stato"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            placeholder="Tutti"
            options={statuses.map((status) => ({ value: status, label: STATUS_LABELS[status] ?? status }))}
          />
        </div>
      </Card>

      <Card title={`${sorted.length} resoconti`} flush>
        {reportsQuery.isPending ? (
          <Spinner label="Caricamento dei resoconti…" />
        ) : reportsQuery.isError ? (
          <Alert variant="error" title="Impossibile caricare i resoconti">
            {reportsQuery.error instanceof ApiError ? reportsQuery.error.message : 'Errore imprevisto.'}
          </Alert>
        ) : sorted.length === 0 ? (
          <EmptyState
            icon="📄"
            title="Nessun resoconto"
            description={
              items.length === 0
                ? 'Nessuna analisi o briefing ancora generato.'
                : 'Nessun resoconto corrisponde ai filtri scelti.'
            }
          />
        ) : (
          <>
            {/* Schede: sotto gli 880px. Vedi il commento in testa al file. */}
            <ul className="reports__cards">
              {sorted.map((item) => (
                <ReportCard key={`${item.kind}-${item.id}`} item={item} />
              ))}
            </ul>

            <TableWrap tall>
              <table className="table table--compact table--sticky-head reports__table">
                <thead>
                  <tr>
                    {SORT_COLUMNS.map((column) => (
                      <SortHeader
                        key={column.key}
                        column={column}
                        active={sortKey === column.key}
                        order={sortOrder}
                        onClick={() => toggleSort(column.key)}
                      />
                    ))}
                    <th>Titolo/Mercato</th>
                    <th className="col--secondary">Titoli correlati</th>
                    <th>Rating</th>
                    <th className="col--tertiary">Origine</th>
                    <th>Catena</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((item) => {
                    const detail = reportTypeDetail(item.kind, item.reportType);
                    return (
                      <tr key={`${item.kind}-${item.id}`} className="reports__row">
                        <td>
                          <Link to={item.detailPath} className="reports__row-link">
                            {formatDateTime(item.createdAt)}
                          </Link>
                        </td>
                        <td>
                          <Badge tone={KIND_TONE[item.kind]}>{KIND_LABELS[item.kind]}</Badge>
                          {/* `null` quando ripeterebbe il badge: per tre generi su
                              quattro `reportType` è una costante implicata da `kind`. */}
                          {detail && <span className="reports__report-type">{detail}</span>}
                        </td>
                        <td className="cell--minor col--secondary">
                          {item.marketMic ? (
                            <span>
                              {item.marketMic}
                              <span className="text-muted"> · {item.marketName}</span>
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <Badge tone={STATUS_TONE[item.status] ?? 'neutral'}>
                            {STATUS_LABELS[item.status] ?? item.status}
                          </Badge>
                        </td>
                        <td>
                          <Link to={item.detailPath}>{item.title}</Link>
                        </td>
                        <td className="col--secondary">
                          <InstrumentChips instruments={item.instruments} />
                        </td>
                        <td>
                          {item.rating ? (
                            <Badge tone={RATING_TONE[item.rating as keyof typeof RATING_TONE]}>
                              {item.rating}
                            </Badge>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="cell--minor col--tertiary">
                          {item.triggeredBy ? (
                            TRIGGERED_BY_LABELS[item.triggeredBy]
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {/* F-105: «e questo dove ha portato». La domanda
                              nasce qui, guardando l'elenco, quindi la porta
                              d'ingresso sta qui e non dentro il documento. */}
                          <Link to={tracePath(item)} className="reports__trace-link">
                            Catena →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrap>
          </>
        )}
      </Card>
    </div>
  );
}
